import "../../../FhDataTable.css";
import { useSlots, ref, computed, onBeforeMount, nextTick, watch, openBlock, createElementBlock, normalizeClass, createBlock, withCtx, renderSlot, createCommentVNode, createElementVNode, createVNode, Fragment, renderList, mergeProps, withKeys, resolveDynamicComponent, createTextVNode, toDisplayString, markRaw } from "vue";
import FhText from "../../FhText/FhText.mjs";
import FhIcon from "../../FhIcon/FhIcon.mjs";
import FhVisuallyHidden from "../../FhVisuallyHidden/FhVisuallyHidden.mjs";
import sortOrderAscending from "../../../icons-internal/sortOrderAscending.mjs";
import sortOrderDescending from "../../../icons-internal/sortOrderDescending.mjs";
import sortOrderNone from "../../../icons-internal/sortOrderNone.mjs";
import { generateUniqueId } from "../../utils.mjs";
import { LAYOUT, SORT_ORDER_ENUMS } from "./constants.mjs";
import FhTableRow from "../parts/FhTableRow/FhTableRow.mjs";
import FhTableCell from "../parts/FhTableCell/FhTableCell.mjs";
import _sfc_main$1 from "../FhDataTableColumn/FhDataTableColumn.mjs";
import FhPagination from "../FhPagination/FhPagination.mjs";
import { _ as _export_sfc } from "../../../_plugin-vue_export-helper-1tPrXgE0.js";
const _hoisted_1 = ["aria-labelledby"];
const _hoisted_2 = {
  key: 1,
  class: "fh-table-foot"
};
const _sfc_main = {
  __name: "FhDataTable",
  props: {
    data: {
      type: Array,
      required: true
    },
    rowsPerPage: {
      type: Number,
      default: void 0
    },
    interactiveRows: {
      type: Boolean,
      default: false
    },
    tableLayout: {
      type: String,
      default: LAYOUT.FIXED,
      validator: (value) => Object.values(LAYOUT).includes(value)
    }
  },
  setup(__props) {
    const props = __props;
    const slots = useSlots();
    const sortField = ref("");
    const sortOrder = ref(SORT_ORDER_ENUMS.NONE);
    const columns = ref([]);
    const currentPage = ref(1);
    const uniqueId = generateUniqueId();
    const componentId = `fh-table-caption--${uniqueId}`;
    const extractSlots = (vnode) => {
      const columnSlots = ["header", "custom"];
      const { name, sortable, align, ...attrs } = vnode.props || {};
      const slotAcc = {
        name,
        sortable,
        align,
        attrs
      };
      columnSlots.forEach((slotName) => {
        var _a;
        if ((_a = vnode.children) == null ? void 0 : _a[slotName]) {
          slotAcc[`${slotName}Slot`] = markRaw({
            ...slotName === "custom" && { props: ["rowData"] },
            render() {
              return slotName === "custom" ? vnode.children[slotName]({ rowData: this.rowData }) : vnode.children[slotName]();
            }
          });
        }
      });
      return slotAcc;
    };
    const generateColumnsFromSlot = (newSlots) => {
      if (!newSlots || newSlots.length === 0 || !newSlots.flatMap) {
        return [];
      }
      return newSlots.flatMap((newSlotItem) => {
        if ((newSlotItem == null ? void 0 : newSlotItem.type) === _sfc_main$1) {
          return extractSlots(newSlotItem);
        }
        return generateColumnsFromSlot(newSlotItem.children || []);
      });
    };
    const generateColumnsFromData = () => {
      if (props.data.length === 0) return [];
      const keys = Object.keys(props.data[0]);
      return keys.map((key) => ({
        name: key,
        key
      }));
    };
    const computedSortedData = computed(() => {
      if (!sortField.value || sortOrder.value === SORT_ORDER_ENUMS.NONE) {
        return props.data;
      }
      return [...props.data].sort((a, b) => {
        if (a[sortField.value] < b[sortField.value])
          return sortOrder.value === SORT_ORDER_ENUMS.ASC ? -1 : 1;
        if (a[sortField.value] > b[sortField.value])
          return sortOrder.value === SORT_ORDER_ENUMS.ASC ? 1 : -1;
        return 0;
      });
    });
    const computedPaginatedData = computed(() => {
      const start = (currentPage.value - 1) * props.rowsPerPage;
      const end = start + props.rowsPerPage;
      return computedSortedData.value.slice(start, end);
    });
    const computedData = computed(() => {
      return props.rowsPerPage ? computedPaginatedData.value : computedSortedData.value;
    });
    const computedShowTable = computed(() => {
      return columns.value.length > 0 && computedData.value.length > 0;
    });
    const computedTotalPages = computed(() => {
      return props.rowsPerPage ? Math.ceil(props.data.length / props.rowsPerPage) : null;
    });
    const computedShowPagination = computed(() => {
      return computedTotalPages.value > 1;
    });
    const computedTableClasses = computed(() => {
      return [
        "fh-table",
        { "fh-table--highlight-rows": props.interactiveRows },
        `fh-table--${props.tableLayout}`
      ];
    });
    const computedTableHeadClasses = computed(() => {
      return ["fh-table-head", { "fh-table-head--multi-row": slots.header }];
    });
    const computedTableBodyClasses = computed(() => {
      return ["fh-table-body", !slots.footer && "fh-table-body-only"];
    });
    const sort = (name) => {
      if (sortField.value === name) {
        if (sortOrder.value === SORT_ORDER_ENUMS.ASC) {
          sortOrder.value = SORT_ORDER_ENUMS.DESC;
        } else if (sortOrder.value === SORT_ORDER_ENUMS.DESC) {
          sortField.value = "";
          sortOrder.value = SORT_ORDER_ENUMS.NONE;
        }
      } else {
        sortField.value = name;
        sortOrder.value = SORT_ORDER_ENUMS.ASC;
      }
    };
    const getSortIcon = (name) => {
      if (sortField.value !== name || sortOrder.value === SORT_ORDER_ENUMS.NONE)
        return sortOrderNone;
      return sortOrder.value === SORT_ORDER_ENUMS.ASC ? sortOrderAscending : sortOrderDescending;
    };
    const getAriaSort = (name) => {
      return sortField.value === name ? sortOrder.value : SORT_ORDER_ENUMS.NONE;
    };
    const handleSlotChange = (newSlots) => {
      columns.value = (newSlots == null ? void 0 : newSlots.length) > 0 ? generateColumnsFromSlot(newSlots) : generateColumnsFromData();
    };
    onBeforeMount(async () => {
      await nextTick();
      handleSlotChange(slots.default ? slots.default() : []);
    });
    watch(
      () => slots.default ? slots.default() : [],
      (newSlots) => handleSlotChange(newSlots)
    );
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: "fh-table-wrapper",
        role: "group",
        "aria-labelledby": _ctx.$slots.caption && componentId
      }, [
        computedShowTable.value ? (openBlock(), createElementBlock("table", {
          key: 0,
          class: normalizeClass(computedTableClasses.value)
        }, [
          _ctx.$slots.caption ? (openBlock(), createBlock(FhVisuallyHidden, {
            key: 0,
            id: componentId,
            alignment: "start",
            tag: "caption"
          }, {
            default: withCtx(() => [
              renderSlot(_ctx.$slots, "caption", {}, void 0, true)
            ]),
            _: 3
          })) : createCommentVNode("", true),
          createElementVNode("thead", {
            class: normalizeClass(computedTableHeadClasses.value)
          }, [
            createVNode(FhTableRow, { class: "fh-table-row--header" }, {
              default: withCtx(() => [
                (openBlock(true), createElementBlock(Fragment, null, renderList(columns.value, (column) => {
                  return openBlock(), createBlock(FhTableCell, mergeProps({
                    key: `header-${column.key}`,
                    role: "columnheader",
                    scope: "col",
                    "aria-sort": getAriaSort(column.name),
                    align: column.align,
                    class: [
                      "fh-table-cell-header",
                      column.sortable && `fh-table-cell-header--sortable`
                    ],
                    "aria-labelledby": column.sortable ? `fh-column-sortable--${column.name}` : void 0,
                    tabindex: column.sortable ? 0 : void 0,
                    onClick: ($event) => column.sortable && sort(column.name),
                    onKeyup: withKeys(($event) => column.sortable && sort(column.name), ["enter"]),
                    ref_for: true
                  }, column.attrs), {
                    default: withCtx(() => [
                      column.headerSlot ? (openBlock(), createBlock(resolveDynamicComponent(column.headerSlot), { key: 0 })) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
                        createTextVNode(toDisplayString(column.name), 1)
                      ], 64)),
                      column.sortable ? (openBlock(), createBlock(FhIcon, {
                        key: 2,
                        size: "md",
                        icon: getSortIcon(column.name)
                      }, null, 8, ["icon"])) : createCommentVNode("", true),
                      column.sortable ? (openBlock(), createBlock(FhVisuallyHidden, {
                        key: 3,
                        id: `fh-column-sortable--${column.name}`
                      }, {
                        default: withCtx(() => [
                          sortField.value === column.name ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
                            column.headerSlot ? (openBlock(), createBlock(resolveDynamicComponent(column.headerSlot), { key: 0 })) : createCommentVNode("", true),
                            createTextVNode(" sorted in " + toDisplayString(sortOrder.value) + " order ", 1)
                          ], 64)) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
                            column.headerSlot ? (openBlock(), createBlock(resolveDynamicComponent(column.headerSlot), { key: 0 })) : createCommentVNode("", true),
                            createTextVNode(" not sorted ")
                          ], 64))
                        ]),
                        _: 2
                      }, 1032, ["id"])) : createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1040, ["aria-sort", "align", "class", "aria-labelledby", "tabindex", "onClick", "onKeyup"]);
                }), 128))
              ]),
              _: 1
            }),
            renderSlot(_ctx.$slots, "header", {}, void 0, true)
          ], 2),
          createElementVNode("tbody", {
            class: normalizeClass(computedTableBodyClasses.value)
          }, [
            createCommentVNode("", true),
            (openBlock(true), createElementBlock(Fragment, null, renderList(computedData.value, (rowData) => {
              return openBlock(), createBlock(FhTableRow, {
                key: rowData.id
              }, {
                default: withCtx(() => [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(columns.value, (column) => {
                    return openBlock(), createBlock(FhTableCell, {
                      tag: "td",
                      key: column.key,
                      align: column.align
                    }, {
                      default: withCtx(() => [
                        column.customSlot ? (openBlock(), createBlock(resolveDynamicComponent(column.customSlot), {
                          key: 0,
                          rowData
                        }, null, 8, ["rowData"])) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
                          createTextVNode(toDisplayString(rowData[column.name]), 1)
                        ], 64))
                      ]),
                      _: 2
                    }, 1032, ["align"]);
                  }), 128))
                ]),
                _: 2
              }, 1024);
            }), 128))
          ], 2),
          _ctx.$slots.footer ? (openBlock(), createElementBlock("tfoot", _hoisted_2, [
            renderSlot(_ctx.$slots, "footer", {}, void 0, true)
          ])) : createCommentVNode("", true)
        ], 2)) : (openBlock(), createBlock(FhText, {
          key: 1,
          class: "fh-data-table-empty",
          variant: "body-md"
        }, {
          default: withCtx(() => [
            createElementVNode("pre", null, "No data " + toDisplayString(props.data), 1)
          ]),
          _: 1
        })),
        computedShowPagination.value ? (openBlock(), createBlock(FhPagination, {
          key: 2,
          totalPages: computedTotalPages.value,
          modelValue: currentPage.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => currentPage.value = $event)
        }, null, 8, ["totalPages", "modelValue"])) : createCommentVNode("", true)
      ], 8, _hoisted_1);
    };
  }
};
const FhDataTable = /* @__PURE__ */ _export_sfc(_sfc_main, [["__scopeId", "data-v-a96da932"]]);
export {
  FhDataTable as default
};
//# sourceMappingURL=FhDataTable.mjs.map
